import { escapeHtml, readShowroomState, reloadWhenReady, showroomRequest, showroomToast } from "./showroom-api";

// <showroom-wishlist>: the "Add from wishlist" modal. Lists the shopper's
// Swym wishlist and adds items (or all of them) to their showroom list.
// Loaded the first time the modal opens.

function fetchWishlistItems() {
  return new Promise((resolve) => {
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push((swat) => {
      swat.fetchLists({
        callbackFn: (lists) => {
          const items = (lists || []).flatMap((list) => list.listcontents || []);
          const seen = new Set();

          resolve(items.filter((item) => item.epi && !seen.has(item.epi) && seen.add(item.epi)));
        },
        errorFn: () => resolve([]),
      });
    });
  });
}

class ShowroomWishlist extends HTMLElement {
  connectedCallback() {
    this.page = this.closest("showroom-page");
    this.modal = this.closest("showroom-modal");
    this.grid = this.querySelector("[data-wishlist-grid]");
    this.addAllButton = this.querySelector("[data-wishlist-add-all]");
    this.count = this.modal?.querySelector("[data-wishlist-count]");
    this.closetVariantIds = new Set((this.dataset.closetVariantIds || "").split(",").filter(Boolean));

    this.modal?.addEventListener("showroom-modal:open", () => this.load());

    this.addEventListener("click", (event) => {
      const addButton = event.target.closest("[data-wishlist-add]");

      if (addButton) this.add([addButton.dataset.wishlistAdd], addButton);
      else if (event.target.closest("[data-wishlist-add-all]")) this.add(this.availableIds(), this.addAllButton);
    });
  }

  availableIds() {
    return (this.items || []).map((item) => String(item.epi)).filter((id) => !this.closetVariantIds.has(id));
  }

  async load() {
    if (this.items) return;

    this.items = await fetchWishlistItems();
    this.render();
  }

  render() {
    if (this.count) this.count.textContent = this.items.length || "";
    this.addAllButton.disabled = this.availableIds().length === 0;

    if (!this.items.length) {
      this.grid.innerHTML = `<li class="showroom-wishlist__empty body">${escapeHtml(this.dataset.localeEmpty)}</li>`;
      return;
    }

    const checkIcon = this.querySelector("template[data-check-icon]")?.innerHTML || "";

    this.grid.innerHTML = this.items
      .map((item) => {
        const [title, subtitle] = String(item.dt || "").split("|").map((part) => part.trim());
        const inShowroom = this.closetVariantIds.has(String(item.epi));
        const url = item.du ? `${item.du}${item.du.includes("?") ? "&" : "?"}variant=${item.epi}` : "#";

        return `
          <li class="showroom-wishlist__item">
            <div class="showroom-wishlist__media">
              <img class="showroom-wishlist__image" src="${escapeHtml(item.iu)}" alt="${escapeHtml(subtitle || title)}" loading="lazy">
            </div>
            <p class="h6">${escapeHtml(title)}</p>
            <p class="body-small">${window.formatMoney ? window.formatMoney(Number(item.pr) || 0) : escapeHtml(item.pr)}</p>
            ${item.vi ? `<p class="caption">${escapeHtml(item.vi)}</p>` : ""}
            ${
              inShowroom
                ? `<p class="showroom-wishlist__added label">${checkIcon}${escapeHtml(this.dataset.localeInShowroom)}</p>`
                : `<button type="button" class="link link--primary" data-wishlist-add="${escapeHtml(item.epi)}">${escapeHtml(this.dataset.localeAdd)}</button>`
            }
            <a href="${escapeHtml(url)}" class="button button--secondary button--full" target="_blank" rel="noopener">
              ${escapeHtml(this.dataset.localeBuyNow)}
            </a>
          </li>
        `;
      })
      .join("");
  }

  async add(variantIds, button) {
    if (!variantIds.length) return;

    button?.classList.add("is-loading");
    if (button) button.disabled = true;

    const body = { email: this.dataset.email };
    if (variantIds.length === 1) body.variant = variantIds[0];
    else body.variants = variantIds.join(",");

    const { ok } = await showroomRequest("addProductVariantToShowroom", { body, showroom: this.page?.dataset.showroomId });

    if (!ok) {
      button?.classList.remove("is-loading");
      if (button) button.disabled = false;
      showroomToast(this.page?.dataset.localeGenericError);
      return;
    }

    // Reloads once the page lists every added style.
    const shown = await reloadWhenReady((doc) => {
      const styles = (readShowroomState(doc).styles || []).map(String);
      return variantIds.every((id) => styles.includes(String(id)));
    });
    if (!shown) {
      button?.classList.remove("is-loading");
      if (button) button.disabled = false;
    }
  }
}

if (!customElements.get("showroom-wishlist")) {
  customElements.define("showroom-wishlist", ShowroomWishlist);
}
